/**
 * A staff member's saved queue views (a name and the queue's query string), kept in this browser only: a per-viewer
 * convenience, never shared and never sent to platform-api. Storage can be missing or throw (private windows, blocked
 * site data), so every read and write is wrapped and the queue works without it.
 */
export interface SavedView {
  id: string;
  name: string;
  /** The queue's search string without the leading ?, for example view=all&labels=<id>. */
  search: string;
}

const PREFIX = 'trace-admin.tickets.views.';
/** Enough for a personal list; older views are kept, new ones past the limit are refused by the caller. */
export const MAX_SAVED_VIEWS = 20;

/** A unique id for a view: crypto.randomUUID where the browser has it (secure contexts), a time-and-random id else. */
function newViewId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // Not available (an insecure context): fall through.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const keyOf = (staffUserId: string) => `${PREFIX}${staffUserId}`;

function isSavedView(value: unknown): value is SavedView {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.name === 'string' && typeof v.search === 'string';
}

export function readSavedViews(staffUserId: string): SavedView[] {
  try {
    const raw = window.localStorage.getItem(keyOf(staffUserId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isSavedView).slice(0, MAX_SAVED_VIEWS) : [];
  } catch {
    // Unreadable or blocked storage: no saved views, the queue's own views still work.
    return [];
  }
}

function write(staffUserId: string, views: SavedView[]): boolean {
  try {
    window.localStorage.setItem(keyOf(staffUserId), JSON.stringify(views));
    return true;
  } catch (error) {
    console.warn('admin-web: the saved ticket views could not be stored in this browser', error);
    return false;
  }
}

/** Adds a view; answers the new list, or null when the browser refused to store it. */
export function saveView(staffUserId: string, name: string, search: string): SavedView[] | null {
  const views = readSavedViews(staffUserId);
  const view: SavedView = { id: newViewId(), name: name.trim(), search };
  const next = [...views, view].slice(-MAX_SAVED_VIEWS);
  return write(staffUserId, next) ? next : null;
}

/** Removes a view; answers the new list, or null when the browser refused the change. */
export function removeView(staffUserId: string, viewId: string): SavedView[] | null {
  const next = readSavedViews(staffUserId).filter((v) => v.id !== viewId);
  return write(staffUserId, next) ? next : null;
}
